import { MainApi } from 'api/MainApi'
import { MissionRobotTypeOption } from 'api/RobotApi'
import { Button } from 'components/Button/Button'
import Dropdown, { IOption } from 'components/Dropdown/Dropdown'
import Title from 'components/Title/Title'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Modal from 'react-modal'
import type { IFacility, IRobotMap } from 'utils/interfaces'

import style from './AddMissionModal.module.css'

interface IDialogProps {
  isOpen: boolean
  robotTypeOptions: MissionRobotTypeOption[]
  onClose: () => void
  onSelect: (robotType: string, name: string, locMode: 'GNSS' | 'SLAM', mapId?: number, mapName?: string) => void
}

const AddMissionModal = ({
  isOpen,
  robotTypeOptions: options,
  onClose,
  onSelect,
}: React.PropsWithChildren<IDialogProps>) => {
  const { t } = useTranslation()

  const onCancelClicked = () => {
    onClose()
  }

  const dropdownOptions: IOption[] = options
    .filter(opt => opt.display_name?.trim())
    .map(opt => ({
      text: opt.display_name,
      value: opt.robot_type,
    }))

  const locModeOptions: IOption[] = [
    { text: 'GNSS', value: 'GNSS' },
    { text: 'SLAM', value: 'SLAM' },
  ]

  const [robotType, setRobotType] = useState((dropdownOptions[0]?.value as string) || '')
  const [robotTypeOption, setRobotTypeOption] = useState<IOption>(dropdownOptions[0] || { text: '', value: '' })
  const [missionName, setMissionName] = useState('')
  const [locMode, setLocMode] = useState<'GNSS' | 'SLAM'>('GNSS')
  const [locModeOption, setLocModeOption] = useState<IOption>(locModeOptions[0])

  const [facilities, setFacilities] = useState<IFacility[]>([])
  const [selectedFacilityId, setSelectedFacilityId] = useState<number | undefined>(undefined)
  const [selectedFacilityOption, setSelectedFacilityOption] = useState<IOption | undefined>(undefined)

  const [maps, setMaps] = useState<IRobotMap[]>([])
  const [selectedMapId, setSelectedMapId] = useState<number | undefined>(undefined)
  const [selectedMapOption, setSelectedMapOption] = useState<IOption | undefined>(undefined)

  // Sync state when options prop changes (component is always mounted, so useState keeps stale initial value)
  useEffect(() => {
    if (dropdownOptions.length > 0) {
      setRobotType(dropdownOptions[0].value as string)
      setRobotTypeOption(dropdownOptions[0])
    }
  }, [options])

  // Reset states and fetch facilities when modal is opened
  useEffect(() => {
    if (isOpen) {
      setMissionName('')
      setLocMode('GNSS')
      setLocModeOption(locModeOptions[0])
      setSelectedFacilityId(undefined)
      setSelectedFacilityOption(undefined)
      setSelectedMapId(undefined)
      setSelectedMapOption(undefined)
      setMaps([])
      MainApi.fetchFacilities(1, 1000)
        .then(res => {
          setFacilities(res.results || [])
        })
        .catch(err => {
          console.error('Failed to fetch facilities in modal', err)
        })
    }
  }, [isOpen])

  // Fetch maps when selectedFacilityId changes
  useEffect(() => {
    if (selectedFacilityId) {
      MainApi.fetchRobotMaps(undefined, 1000, undefined, selectedFacilityId)
        .then(res => {
          setMaps(res.results || [])
        })
        .catch(err => {
          console.error('Failed to fetch robot maps for facility in modal', err)
        })
    } else {
      setMaps([])
    }
  }, [selectedFacilityId])

  const onTypeSelected = (e: IOption) => {
    setRobotTypeOption(e)
    setRobotType((e.value as string) || '')
  }

  const onLocModeSelected = (e: IOption) => {
    setLocModeOption(e)
    const mode = e.value as 'GNSS' | 'SLAM'
    setLocMode(mode)
    if (mode === 'GNSS') {
      setSelectedFacilityId(undefined)
      setSelectedFacilityOption(undefined)
      setSelectedMapId(undefined)
      setSelectedMapOption(undefined)
    }
  }

  const facilityOptions: IOption[] = facilities.map(f => ({
    text: f.name || 'Unnamed Facility',
    value: f.id,
  }))

  const onFacilitySelected = (e: IOption) => {
    setSelectedFacilityOption(e)
    setSelectedFacilityId(e.value as number)
    setSelectedMapId(undefined)
    setSelectedMapOption(undefined)
  }

  const mapOptions: IOption[] = maps.map(m => ({
    text: m.name || m.map_id || 'Unnamed Map',
    value: m.id,
  }))

  const onMapSelected = (e: IOption) => {
    setSelectedMapOption(e)
    setSelectedMapId(e.value as number)
  }

  const onSelectClicked = async () => {
    if (missionName.trim()) {
      const selectedMap = maps.find(m => m.id === selectedMapId)
      onSelect(robotType, missionName.trim(), locMode, selectedMapId, selectedMap?.name || selectedMap?.map_id)
    }
  }

  const isCreateDisabled = !missionName.trim() || (locMode === 'SLAM' && (!selectedFacilityId || !selectedMapId))

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onCancelClicked}
      className={style.root}
      ariaHideApp={false}
      closeTimeoutMS={200}
    >
      <div className={style.modal}>
        <div className={style.head}>
          <Title text="Create a new mission" className={style.title} />
        </div>
        <div className={style.body}>
          <div className={style.formFields}>
            <div className={style.inputField}>
              <label className={style.label} htmlFor="new-mission-name">
                Mission name
              </label>
              <input
                id="new-mission-name"
                className={style.input}
                value={missionName}
                onChange={e => setMissionName(e.target.value)}
                placeholder="Enter mission name"
                autoFocus
              />
            </div>
            <div className={style.inputField}>
              <label className={style.label}>Select robot's type</label>
              <Dropdown
                className={style.dropdownWrapper}
                currentOption={robotTypeOption}
                options={dropdownOptions}
                onClickOption={onTypeSelected}
              />
            </div>
            <div className={style.inputField}>
              <label className={style.label}>Localization Mode</label>
              <Dropdown
                className={style.dropdownWrapper}
                currentOption={locModeOption}
                options={locModeOptions}
                onClickOption={onLocModeSelected}
              />
            </div>
            {locMode === 'SLAM' && (
              <>
                <div className={style.inputField}>
                  <label className={style.label}>Select Facility</label>
                  <Dropdown
                    className={style.dropdownWrapper}
                    currentOption={selectedFacilityOption}
                    placeholder="Select facility"
                    options={facilityOptions}
                    onClickOption={onFacilitySelected}
                  />
                </div>
                <div className={style.inputField}>
                  <label className={style.label}>Select Lidar Map</label>
                  <Dropdown
                    className={style.dropdownWrapper}
                    currentOption={selectedMapOption}
                    placeholder="Select map"
                    options={mapOptions}
                    onClickOption={onMapSelected}
                    disabled={!selectedFacilityId}
                  />
                </div>
              </>
            )}
          </div>
        </div>
        <div className={style.footer}>
          <Button.Outlined className={style.btn} onClick={onCancelClicked}>
            {t('mission_settings.label_cancel')}
          </Button.Outlined>
          <Button.Standart className={style.btn} onClick={onSelectClicked} disabled={isCreateDisabled}>
            Create
          </Button.Standart>
        </div>
      </div>
    </Modal>
  )
}

export default AddMissionModal
